var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { checksColumnsOrder, isColumnOn } from '../utils';
var useManageColumn = function (_a) {
    var columns = _a.columns, gridSelectors = _a.gridSelectors, gridActions = _a.gridActions, onClose = _a.onClose;
    var resetGrid = gridActions.resetGrid, setColumnConfig = gridActions.setColumnConfig, setColumnsOrder = gridActions.setColumnsOrder;
    var columnConfig = gridSelectors.columnConfig, columnsOrder = gridSelectors.columnsOrder;
    var initialColumnsOrder = useMemo(function () { return columns === null || columns === void 0 ? void 0 : columns.map(function (col) { return col.name; }); }, [columns]);
    // the length of columnsOrder is changed after using the setColumnsOrder
    var columnsOrderValue = (columnsOrder === null || columnsOrder === void 0 ? void 0 : columnsOrder.length) ? columnsOrder : initialColumnsOrder;
    // columnsConfigOptions contains switch changes from the manageColumnForm (isHidden || {})
    var _b = useState(__assign({}, (columnConfig !== null && columnConfig !== void 0 ? columnConfig : {}))), columnsConfigOptions = _b[0], setColumnsConfigOptions = _b[1];
    var _c = useState(columnsOrderValue), updatedColumnsOrder = _c[0], setUpdatedColumnsOrder = _c[1];
    var columnsConfigValues = useMemo(function () {
        return columns === null || columns === void 0 ? void 0 : columns.filter(function (column) {
            var _a;
            var isHidden = (_a = columnsConfigOptions === null || columnsConfigOptions === void 0 ? void 0 : columnsConfigOptions[column.name]) === null || _a === void 0 ? void 0 : _a.isHidden;
            if ((column === null || column === void 0 ? void 0 : column.alwaysOn) || typeof isHidden === 'boolean')
                return !isHidden;
            if (!!column.defaultHidden && !!(columnsConfigOptions === null || columnsConfigOptions === void 0 ? void 0 : columnsConfigOptions[column.name]))
                return true;
            else
                return !(column === null || column === void 0 ? void 0 : column.defaultHidden);
        }).map(function (column) { return column.name; });
    }, [columnsConfigOptions, columns]);
    var onConfirmForm = useCallback(function () {
        setColumnConfig === null || setColumnConfig === void 0 ? void 0 : setColumnConfig(columnsConfigOptions);
        setColumnsOrder === null || setColumnsOrder === void 0 ? void 0 : setColumnsOrder(updatedColumnsOrder);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [columnsConfigOptions, updatedColumnsOrder, onClose, setColumnConfig, setColumnsOrder]);
    var resetColumnConfig = useCallback(function () {
        setUpdatedColumnsOrder(initialColumnsOrder);
        resetGrid === null || resetGrid === void 0 ? void 0 : resetGrid();
        onClose === null || onClose === void 0 ? void 0 : onClose();
        // clean columnsConfigOptions
        setColumnsConfigOptions({});
    }, [onClose, resetGrid, initialColumnsOrder, columnConfig]);
    var onCloseForm = useCallback(function () {
        setUpdatedColumnsOrder(columnsOrderValue);
        onClose === null || onClose === void 0 ? void 0 : onClose();
        setColumnsConfigOptions(columnConfig || {});
    }, [columnsOrderValue, onClose]);
    var getSortedColumns = useCallback(function (orderValues, isManageModal) {
        var list = !isManageModal ? columns.filter(function (column) { return isColumnOn(column, columnsConfigOptions); }) : columns;
        var sortFunction = function (col1, col2) {
            var i = (orderValues || []).indexOf(col1.name);
            var j = (orderValues || []).indexOf(col2.name);
            return i > j ? 1 : i < j ? -1 : 0;
        };
        var sortedList = __spreadArray([], list, true).sort(sortFunction);
        return sortedList;
    }, [columnConfig, columnsConfigOptions, columns]);
    var displayColumnsDefinitions = useMemo(function () { return getSortedColumns(columnsOrderValue, false); }, [columnsOrderValue, columns, columnsConfigOptions, getSortedColumns]);
    var manageColumnsFormDefinitions = useMemo(function () { return getSortedColumns(updatedColumnsOrder, true); }, [updatedColumnsOrder]);
    var handleDragEnd = useCallback(function (isManageModal) {
        if (isManageModal === void 0) { isManageModal = false; }
        return function (item) {
            var destination = item.destination, source = item.source;
            var to = destination === null || destination === void 0 ? void 0 : destination.index;
            var from = source === null || source === void 0 ? void 0 : source.index;
            if (to === from || !destination) {
                return;
            }
            var columns = isManageModal ? manageColumnsFormDefinitions : displayColumnsDefinitions.filter(function (item) { return !item.sticky; });
            var newOrderColumns = __spreadArray([], (isManageModal ? updatedColumnsOrder : columnsOrderValue), true);
            var fromIndex = newOrderColumns.findIndex(function (column) { return columns[from].name === column; });
            var toIndex = newOrderColumns.findIndex(function (column) { return columns[to !== null && to !== void 0 ? to : 0].name === column; });
            // move column from a position to the new position
            var column = newOrderColumns[fromIndex];
            newOrderColumns.splice(fromIndex, 1);
            newOrderColumns.splice(toIndex === -1 ? 0 : toIndex, 0, column);
            if (!isManageModal) {
                setColumnsOrder === null || setColumnsOrder === void 0 ? void 0 : setColumnsOrder(newOrderColumns);
            }
            setUpdatedColumnsOrder(newOrderColumns);
        };
    }, [updatedColumnsOrder, columnsOrderValue, setColumnsOrder, manageColumnsFormDefinitions, displayColumnsDefinitions]);
    useEffect(function () {
        checksColumnsOrder(columnsOrderValue, columns);
    }, []);
    return {
        columnsConfigValues: columnsConfigValues,
        setColumnsConfigOptions: setColumnsConfigOptions,
        onConfirmForm: onConfirmForm,
        resetColumnConfig: resetColumnConfig,
        handleDragEnd: handleDragEnd,
        onCloseForm: onCloseForm,
        displayColumnsDefinitions: displayColumnsDefinitions,
        manageColumnsFormDefinitions: manageColumnsFormDefinitions
    };
};
export default useManageColumn;
